import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { useNavigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { isDefinitiveAuthError } from "../utils/authErrors";
import { clearOwnerOnboardingRedirectGuard } from "../utils/locationOnboarding";
import { clearActingLocationUuid } from "../utils/actingLocation";

const AuthContext = createContext();

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const resetAuth = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    Sentry.setUser(null);
    // The next sign-in should re-evaluate the owner→incomplete-location
    // onboarding redirect (DESIGN_DELTA_2026-07-23 §4).
    clearOwnerOnboardingRedirectGuard();
    // The next sign-in (possibly a different user) lands on its own
    // remembered location, not this tab's.
    clearActingLocationUuid();
    if (
      !location.pathname.match(
        /^\/(login|signup|verify-email|forgot-password|change-password)$/,
      )
    ) {
      navigate("/login");
    }
    setIsLoading(false);
  };

  const applyAuthedUser = (user) => {
    setIsAuthenticated(true);
    setCurrentUser(user);
    Sentry.setUser({ id: user.userId });
    setIsLoading(false);
  };

  // Keep the latest resetAuth / applyAuthedUser available to the mount check
  // and the Hub listener without re-running effects. Refs are stable, so the
  // effect dependency lists below stay honest (only stable values referenced).
  const resetAuthRef = useRef(resetAuth);
  resetAuthRef.current = resetAuth;
  const applyAuthedUserRef = useRef(applyAuthedUser);
  applyAuthedUserRef.current = applyAuthedUser;

  // Validate the session once on mount. Transient/network failures are retried
  // with backoff and NEVER downgrade a live session — only a definitive Cognito
  // auth error logs the user out here. A truly-dead session is caught
  // authoritatively by the API-layer 401 handler in AppContext on the next call.
  // Route changes no longer re-validate (that per-navigation re-check was the
  // primary source of transient logouts).
  useEffect(() => {
    const checkAuth = async () => {
      // 6 attempts total (see plan §10 Q2): initial try + 5 backoff retries.
      const backoffs = [300, 900, 1500, 3000, 5000];
      for (let attempt = 0; ; attempt++) {
        try {
          applyAuthedUserRef.current(await getCurrentUser());
          return;
        } catch (error) {
          if (isDefinitiveAuthError(error)) {
            console.error("Auth check definitive failure; logging out", error);
            Sentry.captureMessage("auth.session_reset", {
              level: "warning",
              tags: {
                source: "authcontext_checkauth",
                errorName: error?.name ?? "none",
              },
            });
            resetAuthRef.current();
            return;
          }
          if (attempt >= backoffs.length) {
            // Transient failures exhausted: give up retrying but KEEP the
            // session. This tag quantifies how often the old code would have
            // wrongly bounced the user to /login on a transient blip.
            console.error(
              "Auth check transient failure; keeping session",
              error,
            );
            Sentry.captureMessage("auth.checkauth_transient_kept_session", {
              level: "info",
              tags: {
                errorName: error?.name ?? "unknown",
                attempts: attempt + 1,
              },
            });
            setIsLoading(false);
            return;
          }
          await sleep(backoffs[attempt]);
        }
      }
    };
    checkAuth();
  }, []);

  // Drive auth state off Amplify Hub events instead of re-checking per route.
  useEffect(() => {
    const unsubscribe = Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          try {
            applyAuthedUserRef.current(await getCurrentUser());
          } catch (error) {
            console.error("Hub signedIn: failed to load user", error);
          }
          break;
        case "signedOut":
          // Forced logout from the API layer (#3) reaches AuthContext here.
          Sentry.captureMessage("auth.session_reset", {
            level: "info",
            tags: { source: "hub_signedout" },
          });
          resetAuthRef.current();
          break;
        case "tokenRefresh_failure":
          // A refresh failure can be a mere network blip — do NOT log out
          // blindly. Force one refresh and only reset on a definitive auth
          // error.
          try {
            await fetchAuthSession({ forceRefresh: true });
          } catch (error) {
            if (isDefinitiveAuthError(error)) {
              Sentry.captureMessage("auth.session_reset", {
                level: "warning",
                tags: {
                  source: "hub_tokenrefresh_failure",
                  errorName: error?.name ?? "none",
                },
              });
              resetAuthRef.current();
            } else {
              console.error(
                "Hub tokenRefresh_failure: transient, keeping session",
                error,
              );
            }
          }
          break;
        default:
          break;
      }
    });
    return () => unsubscribe();
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    currentUser,
    resetAuth,
    setIsAuthenticated,
    setIsLoading,
    setCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
